import { RocketOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { Alert, Button, Modal, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';

import { usePermissions } from '../../account/hooks';
import { BILLING_GATE_STRATEGY } from '../shared/gate-strategy';
import { TRIAL_DAYS } from '../shared/trial';

import type { BillingBlockedEventDetail } from '../../../api/axios.instance';

export const SubscribeRequiredModal = () => {
  const skipBillingGate = import.meta.env.VITE_SKIP_BILLING_GATE === 'true';
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManageBilling = can('settings', 'manageBilling');

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (skipBillingGate || BILLING_GATE_STRATEGY !== 'in_app') {
      return;
    }

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<BillingBlockedEventDetail>).detail ?? {};
      if (detail.code === 'NO_SUBSCRIPTION') {
        setOpen(true);
      }
    };

    window.addEventListener('billing:upgradeRequired', handler);
    return () => window.removeEventListener('billing:upgradeRequired', handler);
  }, [skipBillingGate]);

  useEffect(() => {
    const handler = () => setOpen(false);
    window.addEventListener('billing:upgradeResolved', handler);
    return () => window.removeEventListener('billing:upgradeResolved', handler);
  }, []);

  const handleStartTrial = () => {
    setOpen(false);
    void navigate({ to: '/onboarding/billing' });
  };

  if (skipBillingGate || BILLING_GATE_STRATEGY !== 'in_app') {
    return null;
  }

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      title=" "
      footer={null}
      centered
      destroyOnHidden
      width={640}
      styles={{ body: { paddingTop: 32 } }}
    >
      <Alert
        type="info"
        showIcon
        icon={<RocketOutlined />}
        message="This feature is powered by K3X AI."
        description={
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Text>
              Start your {TRIAL_DAYS}-day free trial to unlock it. No charge until the trial ends. Full
              access from day one.
            </Typography.Text>
            {canManageBilling ? (
              <Button type="primary" onClick={handleStartTrial}>
                Start trial
              </Button>
            ) : (
              <Typography.Text type="secondary">
                Ask your admin to start the subscription.
              </Typography.Text>
            )}
          </Space>
        }
      />
    </Modal>
  );
};
